import {
    createColumnHelper,
} from "@tanstack/react-table";

import CrudActions from "components/crud/CrudActions";

const columnHelper =
    createColumnHelper();


/*
|--------------------------------------------------------------------------
| DATE
|--------------------------------------------------------------------------
*/

const formatDate = (value) => {

    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
};


/*
|--------------------------------------------------------------------------
| STATUS
|--------------------------------------------------------------------------
*/

const getStatusClass = (
    status
) => {

    switch (status) {

        case "generated":
            return `
                border-green-200
                bg-green-50
                text-green-700
            `;

        case "generating":
            return `
                border-blue-200
                bg-blue-50
                text-blue-700
            `;

        case "failed":
            return `
                border-red-200
                bg-red-50
                text-red-700
            `;

        default:
            return `
                border-gray-200
                bg-gray-50
                text-gray-700
            `;
    }
};


/*
|--------------------------------------------------------------------------
| COLUMNS
|--------------------------------------------------------------------------
*/

export const statementColumns = ({
    onView,
    onDownload,
} = {}) => [

        // =========================================================
        // DATE
        // =========================================================

        columnHelper.accessor(
            "generatedAt",
            {
                id: "generatedAt",

                header: "Generated",

                cell: (info) => (
                    <span className="whitespace-nowrap">
                        {
                            formatDate(
                                info.getValue()
                            )
                        }
                    </span>
                ),
            }
        ),


        // =========================================================
        // STATEMENT TYPE
        // =========================================================

        columnHelper.accessor(
            "statementType",
            {
                id: "statementType",

                header: "Type",

                cell: (info) => (
                    <span className="capitalize">
                        {
                            String(
                                info.getValue() ||
                                "-"
                            ).replace(
                                /_/g,
                                " "
                            )
                        }
                    </span>
                ),
            }
        ),


        // =========================================================
        // ENTITY
        // =========================================================

        columnHelper.display({

            id: "entity",

            header: "Entity",

            cell: ({ row }) => {

                const item =
                    row.original;

                return (
                    <div>

                        <div
                            className="
                            font-semibold
                            text-navy-700
                        "
                        >
                            {
                                item.entityName ||
                                "-"
                            }
                        </div>

                        <div
                            className="
                            text-xs
                            text-gray-500
                        "
                        >
                            ID: {item.entityId}
                        </div>

                    </div>
                );
            },
        }),


        // =========================================================
        // PERIOD
        // =========================================================

        columnHelper.display({

            id: "period",

            header: "Period",

            cell: ({ row }) => {

                const item =
                    row.original;

                return (
                    <div className="text-sm">

                        <div>
                            {
                                formatDate(
                                    item.fromDate
                                )
                            }
                        </div>

                        <div
                            className="
                            text-xs
                            text-gray-500
                        "
                        >
                            to{" "}
                            {
                                formatDate(
                                    item.toDate
                                )
                            }
                        </div>

                    </div>
                );
            },
        }),


        // =========================================================
        // BALANCE
        // =========================================================

        columnHelper.accessor(
            "closingBalance",
            {
                id: "closingBalance",

                header: "Closing Balance",

                cell: (info) => {

                    const value =
                        info.getValue();

                    if (
                        value === null ||
                        value === undefined
                    ) {
                        return "-";
                    }

                    return (
                        <span className="font-semibold">
                            ₹
                            {Number(
                                value
                            ).toLocaleString(
                                "en-IN",
                                {
                                    minimumFractionDigits:
                                        2,

                                    maximumFractionDigits:
                                        2,
                                }
                            )}
                        </span>
                    );
                },
            }
        ),


        // =========================================================
        // FORMAT
        // =========================================================

        columnHelper.accessor(
            "format",
            {
                id: "format",

                header: "Format",

                cell: (info) => (
                    <span
                        className="
                        rounded-md
                        border
                        border-gray-200
                        bg-gray-50
                        px-2.5
                        py-1
                        text-xs
                        font-semibold
                        uppercase
                        text-gray-700
                    "
                    >
                        {
                            info.getValue() ||
                            "-"
                        }
                    </span>
                ),
            }
        ),


        // =========================================================
        // STATUS
        // =========================================================

        columnHelper.accessor(
            "status",
            {
                id: "status",

                header: "Status",

                cell: (info) => {

                    const status =
                        info.getValue();

                    return (
                        <span
                            className={`
                            inline-flex
                            rounded-full
                            border
                            px-3
                            py-1
                            text-xs
                            font-semibold
                            capitalize
                            ${getStatusClass(
                                status
                            )}
                        `}
                        >
                            {
                                String(
                                    status ||
                                    "-"
                                ).replace(
                                    /_/g,
                                    " "
                                )
                            }
                        </span>
                    );
                },
            }
        ),


        // =========================================================
        // ACTIONS
        // =========================================================

        columnHelper.display({

            id: "actions",

            header: "Actions",

            cell: ({ row }) => {

                const item =
                    row.original;

                const canDownload =
                    item.status ===
                    "generated" &&
                    typeof onDownload ===
                    "function";

                return (
                    <div
                        className="
                    flex
                    items-center
                    gap-2
                "
                    >

                        <CrudActions
                            showView={false}
                            showEdit={false}
                            showDelete={false}
                        />

                        {canDownload && (
                            <button
                                type="button"
                                onClick={() =>
                                    onDownload(
                                        item
                                    )
                                }
                                title="Download Statement"
                                aria-label="Download Statement"
                                className="
                            inline-flex
                            h-9
                            w-9
                            items-center
                            justify-center
                            rounded-lg
                            border
                            border-indigo-200
                            bg-indigo-50
                            text-indigo-600
                            transition-all
                            hover:border-indigo-300
                            hover:bg-indigo-100
                            hover:text-indigo-700
                            focus:outline-none
                            focus:ring-2
                            focus:ring-indigo-200
                        "
                            >
                                ↓
                            </button>
                        )}

                    </div>
                );
            },
        }),

    ];